/**
 * The bundle a kiosk ships when somebody needs to see inside it.
 *
 * The console's only window into a machine nobody can reach. Deliberately a
 * *bundle* and not a stream: a kiosk that shipped every line would spend its
 * uplink on logging instead of on selling, and fill a table nobody can read.
 *
 * A ring buffer in memory, flushed on startup, on request from the console, and
 * after an uncaught error. Nothing here is durable — a bundle that survived a
 * reboot would be a second store competing with the outbox for the same disk,
 * and the outbox is the one that matters.
 */
import { API_BASE, trpc } from '@/api/client'
import { depth } from '@/outbox/outbox'
import { shellInfo, shellLog } from '@/shell/native'

export interface LogLine {
  readonly at: string
  readonly level: string
  readonly message: string
}

/** Bounded at the same number the schema accepts, so nothing is trimmed twice. */
const CAPACITY = 200
const ring: LogLine[] = []

/**
 * `stack` is carried nowhere but the development log.
 *
 * The ring is one line per entry and a bundle with forty frames in it is a
 * bundle nobody reads — but on a laptop the frames are the whole point.
 */
export function record(level: 'info' | 'warn' | 'error', message: string, stack?: string): void {
  ring.push({ at: new Date().toISOString(), level, message: message.slice(0, 2000) })
  if (ring.length > CAPACITY) ring.splice(0, ring.length - CAPACITY)
  // The shell keeps its own rotating file, so a support bundle has both halves
  // even when the kiosk could not reach the server to ship one.
  shellLog(level, message)
  if (level !== 'info') toDevLog(level, message, stack)
}

/**
 * In development, put the failure somewhere other than the browser's console.
 *
 * A React error during `pnpm dev` is visible to the person at the screen and to
 * nothing else, which is why diagnosing one has meant copying it out by hand.
 * The server appends this to `logs/dev.log` beside its own errors, so the page
 * fault and the 500 that caused it are adjacent and in order. Compiled out of
 * a production bundle: `import.meta.env.DEV` is a constant Vite folds away, and
 * with it the whole function and its call.
 */
function toDevLog(level: 'warn' | 'error', message: string, stack?: string): void {
  if (!import.meta.env.DEV) return
  void fetch(`${API_BASE}/__dev/log`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ source: 'page', level, message, ...(stack === undefined ? {} : { stack }) }),
    keepalive: true
  }).catch(() => {
    // The server is the thing that is down. The console still has this.
  })
}

export const lines = (): readonly LogLine[] => [...ring]

/**
 * Ship a bundle.
 *
 * Never throws: a diagnostic that takes a selling kiosk down with it is worse
 * than a diagnostic nobody received.
 */
export async function ship(kind: 'startup' | 'crash' | 'requested' | 'periodic'): Promise<void> {
  try {
    const info = await shellInfo()
    await trpc.devices.report.mutate({
      kind,
      ...(info === undefined
        ? {}
        : { shellVersion: info.version, platform: info.platform, printerKind: info.printerKind }),
      pageVersion: __BUILD_ID__,
      outboxDepth: await depth(),
      problems: info?.problems === undefined ? [] : [...info.problems],
      lines: lines().slice(-200)
    })
  } catch {
    // Offline, unenrolled, or refused. The ring keeps filling and the next
    // attempt carries what this one could not.
  }
}

/**
 * Catch what the application did not.
 *
 * An uncaught error on a kiosk with no browser chrome is a blank screen nobody
 * can diagnose; this is what turns one into something the console can show.
 */
export function watchForCrashes(): () => void {
  const onError = (event: ErrorEvent) => {
    record(
      'error',
      `uncaught: ${event.message} (${event.filename}:${event.lineno})`,
      event.error instanceof Error ? event.error.stack : undefined
    )
    void ship('crash')
  }
  const onRejection = (event: PromiseRejectionEvent) => {
    record(
      'error',
      `unhandled rejection: ${String(event.reason)}`,
      event.reason instanceof Error ? event.reason.stack : undefined
    )
    void ship('crash')
  }

  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)
  return () => {
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
  }
}
